import deepEqual from 'fast-deep-equal';
import { config } from 'api';
import { deslugifyParam, queryToString, Url } from 'utils/url';
import filterAndMapObject from 'utils/filterAndMapObject';
import { routes } from './pageRoutes';

export const BRAND_SHOP_BASE_URL_TRAILING_SLASH_INDEX = 15; // /regulars/shops/ <-

function deslugifyPathParameters(path, parameters) {
  return parameters.filter(Boolean).reduce((arrResult, arrElem) =>
    arrResult.replace(arrElem, deslugifyParam(arrElem)), path);
}

function deslugifyPathParameter(path, parameter) {
  return path.replace(parameter, deslugifyParam(parameter));
}

// regulars brand shops URL comes from scribe in a form of /regulars/shops/<SHOP SLUG FROM SCRIBE>/category-1/(...)
// hence we do not want to deslugify first two path segments
function filterOutRegularsBrandShops(tags) {
  if (tags[0] === 'shops') {
    tags[0] = undefined; // eslint-disable-line no-param-reassign
    tags[1] = undefined; // eslint-disable-line no-param-reassign
  }
  return tags;
}

function deslugifyPath(path, params) {
  return Object.keys(params).reduce((result, name) => {
    if (name === 'tags') {
      const tags = filterOutRegularsBrandShops(params[name]); // brand shops have irregular path handling, since they contain slugs without id from scribe
      return deslugifyPathParameters(path, tags);
    } else {
      const value = params[name];
      return Array.isArray(value) ? deslugifyPathParameters(path, value) : deslugifyPathParameter(path, value);
    }
  }, path);
}

function shouldDeslugifyQueryParam(name) {
  return (
    name !== 'entry' // search, user may enter any value
    && name !== 'price' // for range values, we do not want to remove the first range one
  );
}

function shouldIncludeQueryParamInStringValue(name) {
  return name !== 'display'; // TODO hopefully a temporary solution until we move 'display' to location state :)
}

function filterAndDeslugifyQuery(query) {
  return filterAndMapObject(query, shouldIncludeQueryParamInStringValue, (parameter, value) =>
    (shouldDeslugifyQueryParam(parameter) ? deslugifyParam(value) : value));
}

function buildDeslugifiedQueryString(query) {
  return queryToString(filterAndDeslugifyQuery(query), { encode: false });
}

function buildDeslugifiedRouteString(location, params) {
  const pathname = deslugifyPath(location.pathname, params);
  if (!location.query || Object.keys(location.query).length === 0) {
    return pathname;
  }

  const queryString = buildDeslugifiedQueryString(location.query);
  return `${pathname}?${queryString}`;
}

function replaceParams(path, params, replacement) {
  return Object.keys(params)
    .reduce((result, name) => {
      const value = params[name];
      const pattern = Array.isArray(value) ? value.filter(Boolean).join('|') : value;
      return pattern.length
        ? result.replace(new RegExp(`/(${pattern}).*`), replacement)
        : result;
    }, path);
}

function deslugifyValues(obj, pathname, shouldDeslugifyFunction) {
  const result = {};
  if (obj) {
    Object.keys(obj).forEach(name => {
      const value = obj[name];
      if (Array.isArray(value)) {
        const valueFiltered = value.filter(Boolean);
        if (valueFiltered.length) {
          result[name] = shouldDeslugifyFunction(name)
            ? valueFiltered.map(val => deslugifyParam(val))
            : valueFiltered;
        }
      } else {
        result[name] = shouldDeslugifyFunction(name) ? deslugifyParam(value) : value;
      }
    });
  }
  return result;
}

function buildApiQuery(tags, query) {
  const { sort, filters, customFilters, price, hideOOS, entry, clusterPath, showAllFavourites } = query;
  const queryParams = {};

  if (entry) {
    queryParams.searchTerm = entry;
  }
  if (showAllFavourites) {
    queryParams.showAllFavourites = showAllFavourites;
  }
  if (clusterPath) {
    queryParams.clusterPath = clusterPath;
  }
  if (tags || filters) {
    let resultTags = [];
    if (tags) resultTags = resultTags.concat(tags);

    if (filters) resultTags = resultTags.concat(filters.split(','));
    queryParams.tags = resultTags.join(',');
  }
  if (customFilters) {
    queryParams.customFilters = customFilters;
  }
  if (price) {
    queryParams.priceFilters = price;
  }
  if (sort) {
    queryParams.sort = sort;
  }
  if (hideOOS) {
    queryParams.hideOOS = hideOOS;
  }

  return queryParams;
}

function buildApiUrl(apiPath, apiQuery = {}) {
  return new Url(`${config.apiBasePath}${apiPath}`).addQueryParameters(apiQuery).toString();
}

function getApiBasePaths(pathname, deslugifiedQuery, deslugifiedParams) {
  const urlWithoutParameters = pathname.split('?')[0];
  const segments = urlWithoutParameters.split('/');
  segments.splice(0, 1);
  let routeApiPaths;

  // Nested url api paths, to add next level if (segments[2])... etc. it has to be on top.
  // second level - example.com/first-level/second-level
  if (segments[1]) {
    const segmentRoute = routes.filter(route => segments[0].includes(route.pathname))[0];
    if (segmentRoute && segmentRoute.children) {
      const child = segmentRoute.children.filter(subRoute => segments[1].includes(subRoute.pathname))[0];
      if (child && child.dynamicSegments) {
        routeApiPaths = {
          pageUrl: child.urls.pageUrl + segments[2],
          productsApi: child.urls.productsApi + segments[2],
          navigationApi: child.urls.navigationApi + segments[2],
        };
      } else if (child) {
        routeApiPaths = child.urls;
      } else { // if has children but not covered in routes.
        routeApiPaths = segmentRoute.urls;
      }
    }
    // first level - example.com/first-level
  } else if (segments[0]) {
    const baseRoute = routes.filter(route => segments[0].includes(route.pathname))[0];
    if (baseRoute) {
      routeApiPaths = routes.filter(route => segments[0].includes(route.pathname))[0].urls;
    }
  }
  if (routeApiPaths) {
    return {
      pageUrl: routeApiPaths.pageUrl,
      productsApi: buildApiUrl(routeApiPaths.productsApi, buildApiQuery(deslugifiedParams.tags, deslugifiedQuery)),
      navigationApi: config.apiBasePath + routeApiPaths.navigationApi,
    };
  } else {
    return false;
  }
}

function extractBrandShopBasePath(pathname) {
  const pathnameWithoutQuery = pathname.split('?')[0];
  const index = pathnameWithoutQuery.indexOf('/', BRAND_SHOP_BASE_URL_TRAILING_SLASH_INDEX + 1);
  return index === -1 ? pathnameWithoutQuery : pathnameWithoutQuery.substring(0, index);
}

export default class ProductsPageRoute {
  // routeParams is required only for the nested routes (e.g. product alternatives, recipe products, etc.)
  constructor(location, params, routeParams = params) {
    this.location = location;
    this.params = params;
    const nested = !deepEqual(routeParams, params);
    if (nested) {
      this.basePath = deslugifyPath(replaceParams(location.pathname, routeParams, ''), params);
      this.basePathSections = replaceParams(location.pathname, routeParams, '#').split('#').filter(Boolean);
    } else {
      if (location.pathname.indexOf('/regulars/shops') === 0) { // brand shops have irregular path handling, since they contain slugs without id from scribe
        this.basePath = extractBrandShopBasePath(location.pathname);
      } else {
        this.basePath = replaceParams(location.pathname, params, '');
      }
      this.basePathSections = [this.basePath];
    }

    this.deslugifiedRouteString = buildDeslugifiedRouteString(location, params);
    this.deslugifiedParams = deslugifyValues(params, location.pathname, () => true);
    this.deslugifiedQuery = deslugifyValues(location.query, false, shouldDeslugifyQueryParam);
    this.apiBasePaths = getApiBasePaths(location.pathname, this.deslugifiedQuery, this.deslugifiedParams);
  }

  /**
   * Returns the route's URL in the deslugified param format, i.e. without the names, using only ids, so the
   * value can be used as unique key.
   *
   * @returns {string}
   */
  toString() {
    return this.deslugifiedRouteString;
  }
}
